import { type ApprovedExpenseItemConstructor } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import { type ExpenseStatusEnum } from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import { type ExpenseTypeEnum } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import {
  type AccountCodeTypeEnum,
  type AccountConstructor,
} from '@webapp/platform/finance'
import {
  type ApprovedExpenseItemAccountApiModel,
  type ApprovedExpenseItemApiModel,
  type ApprovedExpenseItemExpenseTypeFieldApiModel,
} from '../api-models/ApprovedExpenseItemApiModel'

/**
 * Reduces the API's expense_type_fields map (single-entry, keyed by the
 * type's enum name) to the display label rendered in the Type column.
 */
export const mapExpenseTypeFieldsToLabel = (
  expenseTypeFields: Record<string, ApprovedExpenseItemExpenseTypeFieldApiModel>
): string => {
  return Object.values(expenseTypeFields)[0]?.name ?? ''
}

export const mapApprovedExpenseItemAccountApiModel = (
  account: ApprovedExpenseItemAccountApiModel
): AccountConstructor => ({
  id: account.id,
  active: account.active,
  accountCode: {
    // Some approved rows embed account_code without an id — the row's select
    // seeds by account id, so a 0 sentinel is display-safe
    id: account.account_code.id ?? 0,
    code: account.account_code.code,
    description: account.account_code.description,
    type: account.account_code.account_type as AccountCodeTypeEnum,
  },
  secondaryCategory: account.department
    ? {
        id: account.department.id,
        name: account.department.name,
        primaryCategory: account.department.branch
          ? {
              id: account.department.branch.id,
              name: account.department.branch.name,
            }
          : null,
      }
    : null,
})

export const mapApprovedExpenseItemApiModel = (
  apiModel: ApprovedExpenseItemApiModel
): ApprovedExpenseItemConstructor => ({
  id: apiModel.id,
  lineNumber: apiModel.line_num,
  merchant: apiModel.merchant,
  amount: apiModel.amount,
  currencyId: apiModel.localCurrency,
  expenseType: apiModel.expense_type as ExpenseTypeEnum,
  expenseTypeLabel: mapExpenseTypeFieldsToLabel(apiModel.expense_type_fields),
  attachment: apiModel.attachment ?? null,
  notes: apiModel.notes ?? null,
  approvedDate: apiModel.approved_datetime,
  requester: {
    id: apiModel.user.id,
    firstName: apiModel.user.firstName,
    lastName: apiModel.user.lastName,
  },
  expenseReport: {
    id: apiModel.expenseReport.id,
    uuid: apiModel.expenseReport.uuid,
    status: apiModel.expenseReport.status as ExpenseStatusEnum,
    createdDate: apiModel.expenseReport.createdDate,
    hasSiblingItems: apiModel.expenseReport.has_sibling_items,
    notes: apiModel.expenseReport.notes ?? null,
  },
  account: mapApprovedExpenseItemAccountApiModel(apiModel.account),
})
